import { Navigate } from "react-router-dom";
import { usePermissions } from "@/hooks/usePermissions";

/**
 * Keeps a signed-in but un-onboarded user out of the app shell.
 *
 * Clerk considers someone signed in the moment they verify their code, but they have no
 * organization until the onboarding wizard creates one. `RequireAuth` only asks Clerk, so anything
 * that put such a user on an app route — the browser Back button out of the wizard, a bookmarked
 * URL, a second tab — left them in a shell with "Unknown User / No organization", every request
 * failing for want of a membership, and no way forward. The wizard's own redirect is the opposite
 * direction (invited users OUT of onboarding), so nothing sent them back.
 *
 * GFG STAFF ARE EXEMPT, not as a special case but because the premise does not apply: their
 * membership is granted at the platform level and `isGfg` is true before any organization is
 * resolved. Sending them to a client onboarding wizard would be nonsense.
 *
 * Renders nothing while permissions load. A spinner for what is usually one cached read would
 * flash on every navigation, and the alternative — rendering the shell first — is the bug.
 *
 * A FAILED permissions call sends them to onboarding too, rather than waiting: `isLoaded` only ever
 * turns true on success, so waiting on it alone is a white screen for as long as the call keeps
 * failing. And the commonest reason it fails is a 401 for a signed-in user whose local row does not
 * exist — someone who has not finished onboarding, which is where they are being sent.
 */
export function RequireOnboarded({ children }: { children: React.ReactNode }) {
  const { organizationId, isGfg, isLoaded, isError, isFetching } = usePermissions();

  if (!isLoaded && !isError) return null;

  if (!isGfg && organizationId == null) {
    // The cache still says "no organization" and a refetch is in flight: wait for its answer.
    // This is the moment right after onboarding — the wizard invalidates these caps and navigates
    // without waiting, so for a beat the cached value is the one from before the organization
    // existed. Redirecting on it would throw the client back into the wizard they just completed.
    if (isFetching) return null;
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}
